<script lang="ts" setup>
import { useRoute, useRouter } from "vue-router";
import Header from "@/components/organisms/common/Header.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { onMounted, ref } from "vue";
import { orderService } from "@/app/services/OrderService";
import { openInNewWindow } from "@/utils/browserUtil";
import { applicationService } from "@/app/services/ApplicationService";
import { useErrorModal } from "@/utils/modal";
import { parseISO } from "@/utils/date";

type NoticeOfEmployment = {
  archivedAt: string;
  archiveId: number;
  version: number;
};

const route = useRoute();
const router = useRouter();
const errorModal = useErrorModal();
const noticeOfEmployments = ref<NoticeOfEmployment[]>([]);
const workTime = ref<string>("労働条件通知書");
const orderTitle = ref<string>("");

onMounted(async () => {
  try {
    const application = await applicationService.fetchApplication(
      Number(route.params.applicationId)
    );

    workTime.value =
      orderService.getFormattedDateWithSlash(application.order) +
      " " +
      applicationService.getFormattedWorkTimeRange(application);
    orderTitle.value = application.order.title;
    const pdfVersions = await orderService.getNoticeOfEmploymentArchiveList(
      Number(route.params.applicationId)
    );
    noticeOfEmployments.value = pdfVersions
      .map(item => {
        return {
          archivedAt: item.archivedAt,
          archiveId: item.archiveId,
          version: item.version
        };
      })
      .sort((a, b) => descSort(a.version, b.version));
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  }
});

const onBack = () => {
  router.back();
};

const downloadPdf = async (archiveId: number) => {
  try {
    const res = await orderService.getArchivedNoticeOfEmploymentPdfUrl(
      Number(route.params.applicationId),
      archiveId
    );
    openInNewWindow(res.url);
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  }
};

const downloadLatestPdf = async () => {
  try {
    const res = await orderService.getFixedNoticeOfEmploymentPdfUrl(
      Number(route.params.applicationId)
    );
    openInNewWindow(res.url);
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  }
};

function descSort(a: number, b: number) {
  return a < b ? 1 : -1;
}
</script>

<template>
  <Header>
    <template v-slot:left>
      <FeatherIcon icon="chevron-left" @click="onBack" />
    </template>
    労働条件通知書
  </Header>

  <div class="px-3 py-4">
    <p class="text-mild text-size-14">{{ workTime }}「{{ orderTitle }}」</p>
    <div class="bg-white border border-outline-color px-3 mt-3 round-4">
      <div>
        <a
          target="_blank"
          role="button"
          class="d-flex justify-content-between align-items-center py-3"
          @click="downloadLatestPdf"
        >
          <p class="text-black text-size-14 text-weight-500">
            最新版
          </p>
          <FeatherIcon icon="chevron-right" class="text-gray-05" />
        </a>
      </div>
      <div
        v-for="noticeOfEmployment in noticeOfEmployments"
        :key="noticeOfEmployment.archiveId"
      >
        <a
          target="_blank"
          role="button"
          class="d-flex justify-content-between align-items-center py-3"
          @click="downloadPdf(noticeOfEmployment.archiveId)"
        >
          <p class="text-black text-size-14 text-weight-500">
            第{{ noticeOfEmployment.version }}版
          </p>
          <FeatherIcon icon="chevron-right" class="text-gray-05" />
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
